import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { EstimateContactLike } from '@/routes/_dashboard/dashboard/estimates/_shared';
import type { TaxResolutionResult } from '@/types/tax';
import { sanitizePostalCodeInput } from '@/lib/postal-code';

interface AddressLike {
  street?: string;
  street2?: string | null;
  city?: string;
  state?: string;
  zip?: string;
}

interface UseEstimateCustomerTaxSyncArgs {
  applyAddress: (address: AddressLike) => void;
  selectContact: (contact: EstimateContactLike) => {
    address: {
      serviceAddress?: AddressLike | null;
    };
  };
  clearContact: () => void;
  resolveTax: (zip: string, city: string, state: string) => Promise<void>;
  clearPendingTaxResolution: () => void;
  setTaxResolution: Dispatch<SetStateAction<TaxResolutionResult | null>>;
}

export function useEstimateCustomerTaxSync({
  applyAddress,
  selectContact,
  clearContact,
  resolveTax,
  clearPendingTaxResolution,
  setTaxResolution,
}: UseEstimateCustomerTaxSyncArgs) {
  const syncTaxForAddress = useCallback(
    (address?: AddressLike | null) => {
      const nextZip = sanitizePostalCodeInput(address?.zip ?? '');
      const nextCity = address?.city ?? '';
      const nextState = (address?.state ?? '').toUpperCase().slice(0, 2);

      if (!nextZip && !nextCity) {
        clearPendingTaxResolution();
        setTaxResolution(null);
        return;
      }

      void resolveTax(nextZip, nextCity, nextState);
    },
    [clearPendingTaxResolution, resolveTax, setTaxResolution],
  );

  const applyAddressWithTax = useCallback(
    (address: AddressLike) => {
      applyAddress(address);
      syncTaxForAddress(address);
    },
    [applyAddress, syncTaxForAddress],
  );

  const selectContactWithTax = useCallback(
    (contact: EstimateContactLike) => {
      const next = selectContact(contact);
      syncTaxForAddress(next.address.serviceAddress);
    },
    [selectContact, syncTaxForAddress],
  );

  const clearContactWithTax = useCallback(() => {
    clearContact();
    clearPendingTaxResolution();
    setTaxResolution(null);
  }, [clearContact, clearPendingTaxResolution, setTaxResolution]);

  return {
    syncTaxForAddress,
    applyAddressWithTax,
    selectContactWithTax,
    clearContactWithTax,
  };
}
